import { create } from 'zustand';
import type { Edge, Node } from '@xyflow/react';
import { buildExecIndex, type ExecIndex } from './execIndex';
import env from '@shared/api/env';
import type { TelemetryEvent } from '@features/flows/api/blast-api-types';

export type UiNodeStatus = 'idle' | 'queued' | 'running' | 'waiting' | 'paused' | 'succeeded' | 'failed' | 'skipped';

export type NodeStream = {
  /** Provider stream channel id (e.g., run:...:node:...) */
  id?: string;
  /** Provider label prefix (e.g., "litellm") */
  label?: string;
  model?: string;
  /** Accumulated visible text (bounded). */
  text: string;
  done?: boolean;
  openedAt?: number;
  lastDeltaAt?: number;
};

// --- Iteration-aware runtime typings ---
export type LogEntry = {
  t: number;
  level?: 'debug' | 'info' | 'warn' | 'error';
  msg: string;
  frameId?: string;
  iteration?: number;
};

export type IterationState = {
  startedAt?: number;
  endedAt?: number;
  inputsPreview?: unknown;
  outputsPreview?: unknown;
  logs: LogEntry[];
};

const MAX_ITERATIONS_PER_NODE = Number(env.MAX_NODE_ITERATIONS || 200);
const MAX_LOGS_PER_ITER = 1_000;

export type NodeRuntime = {
  status: UiNodeStatus;
  attempt: number;
  runCount: number;
  startedAt?: number;
  endedAt?: number;
  durationMs?: number;
  lastInputsPreview?: unknown;
  lastOutputsPreview?: unknown;
  // Last extracted human-readable error for this node (if any)
  lastErrorMessage?: string;
  lastErrorTs?: number;
  // Aggregate log stream across iterations (kept for back-compat)
  logs: LogEntry[];
  // Per-iteration state buckets
  iterations?: Record<number, IterationState>;
  seenIterations?: number[]; // sorted ascending
  /** Live stream buffer for inline preview. */
  stream?: NodeStream;
};

export type RunRuntime = {
  runId?: string;
  flowId?: string;
  status?: 'running' | 'succeeded' | 'failed' | 'canceled';
  execStep: number;
  lastSeq?: number;
};

type State = {
  run: RunRuntime;
  nodes: Record<string, NodeRuntime>;
  lastDoneTsByNode: Record<string, number>;

  orderIndexByEdgeId: Record<string, number>;
  ambiguousEdgeIds: Set<string>;
  execIndex: ExecIndex | null;

  // animations
  animNonceByEdgeId: Record<string, number>;
  lastAnimAtByEdgeId: Record<string, number>;
  animInflight: number;

  setGraph(nodes: Node[], edges: Edge[]): void;
  resetRun(runId?: string, flowId?: string): void;

  applyTelemetry(t: TelemetryEvent): void;
  setRunStatus(st: RunRuntime['status']): void;

  triggerDataAnimationsOnStart(nodeId: string, ts: number): void;
  triggerDataAnimationsOnComplete(nodeId: string, ts: number): void;
};

const RAF = (() => {
  let q: Array<() => void> = [];
  let scheduled = false;
  return (fn: () => void) => {
    q.push(fn);
    if (!scheduled) {
      scheduled = true;
      requestAnimationFrame(() => {
        const tasks = q;
        q = [];
        scheduled = false;
        for (const f of tasks) f();
      });
    }
  };
})();

// Bound stream buffer to prevent runaway memory.
const MAX_STREAM_CHARS = 64_000;

const normalizeAction = (a?: string) => {
  if (!a) return '';
  const x = a.toLowerCase();
  if (x.includes('start')) return 'start';
  if (x.includes('resume')) return 'resume';
  if (x.includes('queue')) return 'queue';
  if (x.includes('wait')) return 'wait';
  if (x.includes('suspend') || x.includes('pause')) return 'suspend';
  if (x.includes('complete') || x.includes('finish')) return 'complete';
  if (x.includes('error') || x.includes('fail')) return 'error';
  if (x.includes('cancel')) return 'cancel';
  if (x.includes('marker') || x.includes('log')) return 'marker';
  return x as any;
};

// Try to extract a human-friendly error message from assorted payload shapes
const extractErrorMessage = (t: any): string => {
  try {
    // Common wrappers
    const p = (t?.payload ?? t?.error ?? t) as any;
    if (p && typeof p === 'object') {
      if (p.error && typeof p.error === 'object' && typeof p.error.message === 'string') return p.error.message;
      if (typeof p.message === 'string') return p.message;
      if (typeof p.error === 'string') return p.error;
    }
    const sc = t?.scope;
    if (sc && typeof sc === 'object') {
      if (sc.error && typeof sc.error.message === 'string') return sc.error.message;
      if (typeof sc.message === 'string') return sc.message;
    }
  } catch {}
  return 'Node error';
};

function bumpAnim(edgeId: string, setFn: (updater: any) => void, get: () => State) {
  const now = Date.now();
  const last = get().lastAnimAtByEdgeId[edgeId] || 0;
  if (now - last < 300) return;
  if (get().animInflight >= 100) return;
  setFn((s: State) => {
    const lastAnimAtByEdgeId = { ...s.lastAnimAtByEdgeId, [edgeId]: now };
    const animNonceByEdgeId = { ...s.animNonceByEdgeId, [edgeId]: (s.animNonceByEdgeId[edgeId] || 0) + 1 };
    return { lastAnimAtByEdgeId, animNonceByEdgeId, animInflight: s.animInflight + 1 } as Partial<State>;
  });
  setTimeout(() => setFn((s: State) => ({ animInflight: Math.max(0, s.animInflight - 1) } as Partial<State>)), 350);
}

export const useRuntimeStore = create<State>()((set, get) => ({
  run: { execStep: 0 },
  nodes: {},
  lastDoneTsByNode: {},

  orderIndexByEdgeId: {},
  ambiguousEdgeIds: new Set<string>(),
  execIndex: null,

  animNonceByEdgeId: {},
  lastAnimAtByEdgeId: {},
  animInflight: 0,

  setGraph(nodes, edges) {
    set({
      execIndex: buildExecIndex(nodes, edges),
      orderIndexByEdgeId: {},
      ambiguousEdgeIds: new Set<string>(),
    });
  },

  resetRun(runId, flowId) {
    set({
      run: { runId, flowId, status: undefined, execStep: 0, lastSeq: undefined },
      nodes: {},
      lastDoneTsByNode: {},
      orderIndexByEdgeId: {},
      ambiguousEdgeIds: new Set<string>(),
      animNonceByEdgeId: {},
      lastAnimAtByEdgeId: {},
      animInflight: 0,
    });
  },

  setRunStatus(st) {
    set((s) => ({ run: { ...s.run, status: st } }));
  },

  applyTelemetry(t) {
    const a = normalizeAction(t.action || (t as any).type || (t as any).state);
    const nodeId = (t as any).nodeId || (t.scope as any)?.nodeId || (t.scope as any)?.node?.id;
    const ts = typeof t.ts === 'number' ? t.ts : Date.now();
    const seq = (t.scope as any)?.seq;
    const iter = resolveIteration(t);

    RAF(() => {
      set((s) => {
        const nextRun: RunRuntime = { ...s.run };
        if (typeof seq === 'number') nextRun.lastSeq = seq;

        let nextNodes = s.nodes;
        let nextLastDone = s.lastDoneTsByNode;
        let nextOrder = s.orderIndexByEdgeId;
        let nextAmbig = s.ambiguousEdgeIds;

        if (nodeId) {
          const cur: NodeRuntime = s.nodes[nodeId] || { status: 'idle', attempt: 0, runCount: 0, logs: [], iterations: {}, seenIterations: [] };
          let updated: NodeRuntime = { ...cur };
          // Ensure per-iteration buckets
          if (typeof iter === 'number') {
            const iters = updated.iterations ?? {};
            if (!iters[iter]) iters[iter] = { logs: [] };
            const seen = new Set(updated.seenIterations ?? []);
            seen.add(iter);
            const ordered = Array.from(seen).sort((a, b) => a - b);
            updated.iterations = iters;
            updated.seenIterations = ordered.slice(-MAX_ITERATIONS_PER_NODE);
          }

          if (a === 'queue') {
            updated.status = 'queued';
          } else if (a === 'start' || a === 'resume') {
            updated.status = 'running';
            updated.attempt = (updated.attempt || 0) + 1;
            updated.startedAt = ts;
            // Reset any previous stream for a fresh attempt
            updated.stream = undefined;
            nextRun.execStep = (nextRun.execStep || 0) + 1;

            // Per-iteration: capture start + inputs preview if provided
            const sc = (t.scope || {}) as any;
            if (typeof iter === 'number') {
              const it = (updated.iterations as Record<number, IterationState>)[iter] ?? { logs: [] };
              it.startedAt = ts;
              if (typeof sc.inputs !== 'undefined') it.inputsPreview = sc.inputs;
              (updated.iterations as Record<number, IterationState>)[iter] = it;
            }

            // infer exec edge order index on start/resume
            const idx = s.execIndex;
            if (idx) {
              let best = -Infinity;
              let candEdges: string[] = [];
              const incoming = idx.incomingExecByNode[nodeId] || [];
              for (const eid of incoming) {
                const src = idx.edgeById[eid]?.source;
                const done = s.lastDoneTsByNode[src] ?? -Infinity;
                if (done < ts && done > best) {
                  best = done;
                  candEdges = [eid];
                } else if (done === best) {
                  candEdges.push(eid);
                }
              }
              if (candEdges.length === 1) {
                nextOrder = { ...nextOrder, [candEdges[0]]: nextRun.execStep };
              } else if (candEdges.length > 1) {
                nextAmbig = new Set(nextAmbig);
                candEdges.forEach((id) => nextAmbig.add(id));
              }
            }

            // trigger incoming data animations
            get().triggerDataAnimationsOnStart(nodeId, ts);
          } else if (a === 'wait') {
            updated.status = 'waiting';
          } else if (a === 'suspend') {
            updated.status = 'paused';
          } else if (a === 'complete') {
            updated.status = 'succeeded';
            updated.runCount = (updated.runCount || 0) + 1;
            updated.endedAt = ts;
            if (updated.startedAt) updated.durationMs = Math.max(0, ts - updated.startedAt);
            nextLastDone = { ...nextLastDone, [nodeId]: ts };
            get().triggerDataAnimationsOnComplete(nodeId, ts);
            // Finalize stream if it exists
            if (updated.stream && !updated.stream.done) {
              updated.stream = { ...updated.stream, done: true };
            }

            // Per-iteration: capture end + outputs preview if provided
            const sc = (t.scope || {}) as any;
            if (typeof iter === 'number') {
              const it = (updated.iterations as Record<number, IterationState>)[iter] ?? { logs: [] };
              it.endedAt = ts;
              if (typeof sc.outputs !== 'undefined') it.outputsPreview = sc.outputs;
              (updated.iterations as Record<number, IterationState>)[iter] = it;
            }
          } else if (a === 'error') {
            updated.status = 'failed';
            updated.runCount = (updated.runCount || 0) + 1;
            updated.endedAt = ts;
            if (updated.startedAt) updated.durationMs = Math.max(0, ts - updated.startedAt);
            nextLastDone = { ...nextLastDone, [nodeId]: ts };
            // capture human-readable error text for UI tooltips
            try {
              updated.lastErrorMessage = extractErrorMessage(t);
              updated.lastErrorTs = ts;
            } catch {}
            // Finalize stream if it exists
            if (updated.stream && !updated.stream.done) {
              updated.stream = { ...updated.stream, done: true };
            }
          } else if (a === 'cancel') {
            updated.status = 'skipped';
            updated.runCount = (updated.runCount || 0) + 1;
            updated.endedAt = ts;
            if (updated.startedAt) updated.durationMs = Math.max(0, ts - updated.startedAt);
            nextLastDone = { ...nextLastDone, [nodeId]: ts };
            if (updated.stream && !updated.stream.done) {
              updated.stream = { ...updated.stream, done: true };
            }
          } else if (a === 'marker') {
            const payload = (t as any).payload ?? (t as any).scope?.payload ?? {};
            const topLabel = (t as any).label;
            const label = typeof payload.label === 'string' ? payload.label : topLabel;
            const isOpen = !!label && label.endsWith(':open');
            const isDelta = !!label && label.endsWith(':delta');
            const isDone = !!label && label.endsWith(':done');

            if (isOpen || isDelta || isDone) {
              // Generic stream handler, provider-agnostic (e.g., "litellm:*")
              const sid = payload.id ?? payload.streamId;
              const model = payload.model;
              const token =
                typeof payload.t === 'string'
                  ? payload.t
                  : typeof payload.delta === 'string'
                  ? payload.delta
                  : typeof payload.text === 'string'
                  ? payload.text
                  : '';

              const prev = updated.stream ?? { text: '' };
              let nextText = prev.text || '';
              if (isOpen) {
                nextText = '';
              }
              if (isDelta && token) {
                nextText = (nextText + token).slice(-MAX_STREAM_CHARS);
              }
              updated.stream = {
                id: sid ?? prev.id,
                label: (label || prev.label || '').split(':')[0] || prev.label,
                model: model ?? prev.model,
                text: nextText,
                done: isDone ? true : prev.done,
                openedAt: prev.openedAt ?? ts,
                lastDeltaAt: isDelta ? ts : prev.lastDeltaAt,
              };
            } else {
              // Non-stream markers go to logs
              try {
                const level = (payload.level ?? 'info') as any;
                const line = payload.line ?? payload.message ?? JSON.stringify(payload);
                const entry: LogEntry = { t: ts, level, msg: String(line), frameId: (t as any).frameId, iteration: typeof iter === 'number' ? iter : undefined };
                const logs = (updated.logs || []).concat([entry]).slice(-500);
                updated = { ...updated, logs };

                // Per-iteration logs bucket
                if (typeof iter === 'number') {
                  const it = (updated.iterations as Record<number, IterationState>)[iter] ?? { logs: [] };
                  const arr = (it.logs || []).concat([entry]);
                  it.logs = arr.length > MAX_LOGS_PER_ITER ? arr.slice(-MAX_LOGS_PER_ITER) : arr;
                  (updated.iterations as Record<number, IterationState>)[iter] = it;
                }
              } catch {}
            }
          }

          // previews from telemetry scope
          const sc = (t.scope || {}) as any;
          if (typeof sc.inputs !== 'undefined') updated.lastInputsPreview = sc.inputs;
          if (typeof sc.outputs !== 'undefined') updated.lastOutputsPreview = sc.outputs;

          nextNodes = { ...nextNodes, [nodeId]: updated };
        }

        return {
          run: nextRun,
          nodes: nextNodes,
          lastDoneTsByNode: nextLastDone,
          orderIndexByEdgeId: nextOrder,
          ambiguousEdgeIds: nextAmbig,
        } as Partial<State>;
      });
    });
  },

  triggerDataAnimationsOnStart(nodeId) {
    const idx = get().execIndex;
    if (!idx) return;
    const inc = idx.incomingDataByNode[nodeId] || [];
    for (const eid of inc) bumpAnim(eid, set, get);
  },

  triggerDataAnimationsOnComplete(nodeId) {
    const idx = get().execIndex;
    if (!idx) return;
    const out = idx.outgoingDataByNode[nodeId] || [];
    for (const eid of out) bumpAnim(eid, set, get);
  },
}));

function resolveIteration(evt: any): number | undefined {
  try {
    if (typeof evt?.iteration === 'number') return evt.iteration;
    if (typeof evt?.payload?.iteration === 'number') return evt.payload.iteration;
    if (typeof evt?.scope?.iteration === 'number') return evt.scope.iteration;
    // Parse from loop group tokens like "comp_node.loop:gxoq2h_3_4" → 3
    const groups = evt?.groups;
    if (Array.isArray(groups)) {
      for (const g of groups) {
        const m = /_(\d+)_/.exec(String(g));
        if (m) return Number(m[1]);
      }
    }
  } catch {}
  return undefined;
}

// ---------- Derived selectors (status sets + latest error map) ----------
export type NodeStatusSets = {
  running: Set<string>;
  waiting: Set<string>;
  suspended: Set<string>;
  completed: Set<string>;
  failed: Set<string>;
};

export const selectNodeStatusSets = (s: State): NodeStatusSets => {
  const running = new Set<string>();
  const waiting = new Set<string>();
  const suspended = new Set<string>();
  const completed = new Set<string>();
  const failed = new Set<string>();
  for (const [id, n] of Object.entries(s.nodes)) {
    switch (n.status) {
      case 'running': running.add(id); break;
      case 'waiting': waiting.add(id); break;
      case 'paused': suspended.add(id); break;
      case 'succeeded': completed.add(id); break;
      case 'failed': failed.add(id); break;
      default: break;
    }
  }
  return { running, waiting, suspended, completed, failed };
};

export const selectLatestErrorByNode = (s: State): Map<string, string> => {
  const m = new Map<string, string>();
  for (const [id, n] of Object.entries(s.nodes)) {
    if (n.lastErrorMessage) m.set(id, n.lastErrorMessage);
  }
  return m;
};
